import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../i18n/LocalizedLink';
import '../styles/NotFound.css';

const NotFound: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    // Asegurar que el meta tag robots esté configurado para no indexar
    const metaRobots = document.getElementById('robots-meta') as HTMLMetaElement;
    if (metaRobots) {
      metaRobots.content = 'noindex, nofollow';
    }
    
    // Restaurar el valor original cuando el componente se desmonte
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('notFound.seo.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="description" content={t('notFound.seo.description')} />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-br from-indigo-50 via-white to-blue-50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900">
        <div className="w-full max-w-2xl text-center rounded-2xl border border-gray-200 bg-white/80 backdrop-blur p-8 sm:p-10 shadow-xl dark:border-gray-800 dark:bg-gray-900/50">
          <div className="text-7xl sm:text-8xl mb-4 not-found-float">🕵️</div>

          <h1 className="text-7xl sm:text-8xl font-extrabold tracking-tight leading-none bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent">
            404
          </h1>

          <h2 className="mt-4 text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
            {t('notFound.title')}
          </h2>

          <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-300">
            {t('notFound.description')}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <LocalizedLink
              to="/"
              className="inline-flex items-center justify-center rounded-lg px-6 py-3 font-semibold text-white bg-gradient-to-r from-rose-500 to-pink-500 hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-950"
            >
              {t('notFound.backHome')}
            </LocalizedLink>

            <LocalizedLink
              to="/blog"
              className="inline-flex items-center justify-center rounded-lg px-6 py-3 font-semibold border border-gray-300 text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800/50 dark:focus-visible:ring-offset-gray-950"
            >
              {t('notFound.goBlog')}
            </LocalizedLink>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
